import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatConfidence, isScore, ruleScoresOf } from '../action-log-format';
import type { SocialActionLogRow } from '../types';

/**
 * How each rule scored for one comment — Jev rows only.
 *
 * The confidence pill says the winner reached 66%; it cannot say that a near-identical rule took
 * the other 33%, which is what a reader needs to understand a block. The bars answer that, with a
 * tick at the floor the winning action had to clear.
 */

const ScoreBar = ({ probability, threshold, isWinner }: { probability: number; threshold: number | null; isWinner: boolean }) => (
	<div className='relative h-1.5 rounded-full bg-gray-100'>
		<div
			className={cn('h-1.5 rounded-full', isWinner ? 'bg-ink-strong' : 'bg-gray-300')}
			style={{ width: `${Math.round(probability * 100)}%` }}
		/>
		{threshold !== null && (
			<div
				aria-hidden='true'
				className='absolute -top-[3px] h-3 w-px bg-gray-800'
				style={{ left: `${Math.round(threshold * 100)}%` }}
			/>
		)}
	</div>
);

const ScoreLine = ({ label, probability, threshold, isWinner }: { label: string; probability: number; threshold: number | null; isWinner: boolean }) => (
	<>
		<span className={cn('flex min-w-0 items-center gap-1 truncate', isWinner ? 'font-medium text-ink' : 'text-gray-900')} title={label}>
			{isWinner && <Check aria-label='Rule applied' className='size-3.5 shrink-0' />}
			<span className='truncate'>{label}</span>
		</span>
		<ScoreBar probability={probability} threshold={threshold} isWinner={isWinner} />
		<span className={cn('text-right tabular-nums', isWinner ? 'font-medium text-ink' : 'text-gray-900')}>
			{formatConfidence(probability)}
		</span>
	</>
);

export const RuleScores = ({ row }: { row: SocialActionLogRow }) => {
	const ruleScores = ruleScoresOf(row);
	if (!ruleScores) return null;

	const threshold = isScore(row.riskThreshold) ? row.riskThreshold : null;

	return (
		<section aria-label='How the rules scored' className='flex flex-col gap-2.5 py-2'>
			<div className='flex items-baseline justify-between'>
				<span className='text-[13px] text-gray-900'>How the rules scored</span>
				{threshold !== null && <span className='text-[11px] text-gray-800'>needed {formatConfidence(threshold)}</span>}
			</div>
			<div className='grid grid-cols-[minmax(0,1fr)_120px_36px] items-center gap-x-2.5 gap-y-2 text-xs'>
				{ruleScores.scores.map((score) => (
					<ScoreLine key={score.label} {...score} threshold={threshold} />
				))}
				{ruleScores.noRuleProbability !== null && (
					<ScoreLine label='No rule matches' probability={ruleScores.noRuleProbability} threshold={null} isWinner={false} />
				)}
			</div>
			<p className='text-[11px] text-gray-800'>
				Scores are shared across the rules, so two rules describing the same kind of comment split one score between them.
			</p>
		</section>
	);
};
